import Dexie, { type EntityTable } from 'dexie';

import { Logger } from '@eluve/logger';

import { UploadType } from './types';

export type AppointmentFileIdbRow = {
  id: string;
  appointmentId: string;
  lastUploadAttemptedAt: string;
  attemptCount: number;
  wasDegraded: boolean;
  segmentId?: string;
  chunkNum?: number;
  uploadType?: UploadType;
  chunkDurationMinutes?: number;
  shouldBeTranscribed: boolean | undefined;
  /** Tab ID that owns this file. Starts as the creating tab, transfers on orphan recovery [ENG-3289] */
  ownedByTabId?: string;
  /**
   * Chunk write lifecycle. 'recording' = the worker is still writing this
   * file to OPFS (the sync machine must not upload it while its owning tab is
   * alive); 'final' = the chunk is closed and safe to upload. Rows without
   * the field predate this marker (or come from the V1 stack) and are treated
   * as syncable, preserving legacy behavior.
   */
  writeState?: 'recording' | 'final';
  /**
   * Raw write-ahead backup file from the blob+chunked dual-write. Post-parity
   * the chunks already carry the full audio, so a stale backup is redundant
   * unless the session degraded (`wasDegraded`): recovery sweeps DELETE
   * un-degraded backups instead of uploading them. Absent on old rows / v1 /
   * pcm batch files — treated as false (upload as before).
   */
  isWriteAheadBackup?: boolean;
};

export type MetaIdbRow = {
  key: string;
  value: string;
};

/**
 * One produced audio file recorded in an appointment's manifest. [ENG-4717]
 *
 * `chunkNum === null` means a batch-mode file (single file per segment);
 * non-null means a chunk-mode file — a segment then has N entries sharing the
 * same `recordingStartedAt` (group by it to roll the chunks back up to one
 * segment).
 *
 * The stats are stamped once the file is finalized (null until then, so an
 * aborted-before-finalize file is still recorded but visibly incomplete). They
 * cross-check each other to surface dropped audio — `packetCount × ~30ms/frame`
 * is the expected audio length; if that is far below the wall-clock span
 * (`lastPacketAt − firstPacketAt`), packets were lost despite the recording
 * running.
 */
export type AppointmentManifestFile = {
  fileName: string;
  recordingStartedAt: string;
  chunkNum: number | null;
  /** Final encoded size in bytes; 0 means the file was finalized empty. */
  byteSize: number | null;
  /** Total audio packets written to this file. */
  packetCount: number | null;
  /** ISO timestamp of the first packet written to this file. */
  firstPacketAt: string | null;
  /** ISO timestamp of the last packet written to this file. */
  lastPacketAt: string | null;
  /**
   * Degraded write-ahead backup: chunking stopped mid-segment and this file is
   * the segment's only complete audio. Flows to the server at completion so
   * reconciliation protects it instead of cancelling it as a phantom. Absent
   * on old entries — treated as false.
   */
  wasDegraded?: boolean | null;
};

/**
 * [ENG-4717] Durable per-appointment record of the audio files this device
 * actually produced. Survives the deletion of the audio bytes + `appointmentFiles`
 * rows (which are cleaned up after upload) so it can be sent to the server at
 * completion as the authoritative list of what to expect. Accumulates across
 * pause/restart cycles. A TTL sweep removes stale entries.
 */
export type AppointmentManifestIdbRow = {
  appointmentId: string;
  files: AppointmentManifestFile[];
  updatedAt: string;
};

/**
 * [ENG-4717] How long a completed appointment's manifest is kept locally before
 * the TTL sweep removes it. Generous because the row is tiny and we only need it
 * until the server has acknowledged completion.
 */
export const APPOINTMENT_MANIFEST_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const createDb = ({
  userId,
  tenantId,
  logger,
}: {
  userId: string;
  tenantId: string;
  logger: Logger;
}) => {
  const db = new Dexie(`${userId}-${tenantId}`) as Dexie & {
    appointmentFiles: EntityTable<AppointmentFileIdbRow, 'id'>;
    meta: EntityTable<MetaIdbRow, 'key'>;
    appointmentManifests: EntityTable<
      AppointmentManifestIdbRow,
      'appointmentId'
    >;
  };

  db.version(1).stores({
    appointmentFiles: 'id, appointmentId, lastUploadAttemptedAt, attemptCount',
  });

  db.version(2).stores({
    appointmentFiles: 'id, appointmentId, lastUploadAttemptedAt, attemptCount',
    meta: 'key',
  });

  db.version(3).stores({
    appointmentFiles: 'id, appointmentId, lastUploadAttemptedAt, attemptCount',
    meta: 'key',
    appointmentManifests: 'appointmentId, updatedAt',
  });

  db.open().then(() => {
    logger.info('Dexie opened and migrated');

    // [ENG-4717] Best-effort TTL sweep of stale appointment manifests on every
    // db open. Cheap (tiny rows) and covers every db-creation site (web app +
    // extension offscreen) without wiring into a periodic machine.
    const cutoff = new Date(
      Date.now() - APPOINTMENT_MANIFEST_TTL_MS,
    ).toISOString();
    db.appointmentManifests
      .where('updatedAt')
      .below(cutoff)
      .delete()
      .then((deleted) => {
        if (deleted > 0) {
          logger.info('Swept stale appointment manifests', { deleted });
        }
      })
      .catch((error) =>
        logger.warn('Failed to sweep stale appointment manifests', { error }),
      );
  });

  return db;
};

export type DexieDb = ReturnType<typeof createDb>;
